import { Box, Chip, Divider, IconButton, MenuItem, Select, TextField } from "@material-ui/core";
import { ClearRounded as ClearIcon, Search as SearchIcon, Check } from "@material-ui/icons";
import { useMapThemeClasses } from "apps/platform-client/client/src/hooks/use-map-theme-styles";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useStyles from "../../../../components/LyfeTable/use-styles";
import { RoleOptions, TasksTimeframe } from "apps/platform-client/client/src/models/enums/enums";
import Quests from "../quests/Root";
import { useRecoilValue } from "recoil";
import { userPersonalInfoState } from "apps/platform-client/client/src/store/user";
import {
	getBasicQuestFilter,
	searchItem,
	setMyRoleFilters,
	setTimeframeFilters,
	setUnresolvedFilters,
} from "./inbox-quests-filter-service";
import { QuestsType } from "apps/platform-client/client/src/models/notifications";
import { useDebounce } from "@lyfegen-platform/react-common";
import { MS_UNTIL_QUERY_CALL } from "apps/platform-client/client/src/constants/util-constants";
interface QuestTabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

const QuestsTabPanel: React.FC<QuestTabPanelProps> = (props: QuestTabPanelProps) => {
	const { value, index, ...other } = props;
	const { t } = useTranslation();
	const { search } = useMapThemeClasses();
	const [searchInput, setSearchInput] = useState("");
	const { assignedToMeChip, selectField, checkLabel, selectFieldQuest } = useStyles({});
	const [unresolvedFilter, setUnresolvedFilter] = useState(false);
	const [timeframeFilter, setTimeframeFilter] = useState<string[]>([TasksTimeframe.AnyTime]);
	const [categoryFilter, setCategoryFilter] = useState<QuestsType[]>([QuestsType.AnyCategory]);
	const [myRoleFilter, setMyRoleFilter] = useState<RoleOptions[]>([RoleOptions.AnyRole]);
	const userData = useRecoilValue(userPersonalInfoState);
	const taskTimeframeOptions = [TasksTimeframe.AnyTime, TasksTimeframe.Today, TasksTimeframe.ThisWeek, TasksTimeframe.ThisMonth];
	const categoryOptions = [
		QuestsType.AnyCategory,
		QuestsType.UncoveredClaims,
		QuestsType.PotentialDiscontinuation,
		QuestsType.UnmatchedRecords,
	];
	const myRoleOptions = [RoleOptions.AnyRole, RoleOptions.AssignedToMe, RoleOptions.OwnedByMe];
	const [questFilter, setQuestFilter] = useState(
		getBasicQuestFilter([QuestsType.UncoveredClaims, QuestsType.PotentialDiscontinuation, QuestsType.UnmatchedRecords])
	);
	const debouncedTerm = useDebounce(searchInput, MS_UNTIL_QUERY_CALL);

	function refreshFilters(myRoleFilter: any, timeFrameF: any, catFilter: any, unresFilter: any, searchValue: string) {
		const basicQuestUserFilter = getBasicQuestFilter(
			catFilter.length && !catFilter.includes(QuestsType.AnyCategory)
				? catFilter
				: [QuestsType.UncoveredClaims, QuestsType.PotentialDiscontinuation, QuestsType.UnmatchedRecords]
		);
		const myRoleFiltersApplied = setMyRoleFilters(myRoleFilter, userData.id, basicQuestUserFilter);
		const timeframeFiltersApplied = setTimeframeFilters(timeFrameF, myRoleFiltersApplied);
		const unresolvedFiltersApplied = setUnresolvedFilters(unresFilter, timeframeFiltersApplied);
		const searchTermQuestApplied = searchItem(searchValue, unresolvedFiltersApplied, userData.preferredLanguage);
		setQuestFilter(searchTermQuestApplied);
	}

	function clearInput() {
		setSearchInput("");
	}
	function handleSearchInput(e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) {
		setSearchInput(e.target.value || "");
	}

	function handleUnresolved() {
		setUnresolvedFilter(!unresolvedFilter);
		refreshFilters(myRoleFilter, timeframeFilter, categoryFilter, !unresolvedFilter, "");
	}

	useEffect(() => {
		refreshFilters(myRoleFilter, timeframeFilter, categoryFilter, unresolvedFilter, debouncedTerm || "");
	}, [debouncedTerm]);

	function handleTimeframeFilterOptions(e: React.ChangeEvent<any>) {
		const {
			target: { value },
		} = e;
		const curValue = timeframeFilter.some((s) => s === value) ? [] : [value];
		setTimeframeFilter(curValue);
		refreshFilters(myRoleFilter, curValue.length ? value : undefined, categoryFilter, unresolvedFilter, searchInput);
	}

	function handleCategoryFilterOptions(e: React.ChangeEvent<any>) {
		const {
			target: { value },
		} = e;
		const curValue = categoryFilter.some((s) => s === value) ? [] : [value];
		setCategoryFilter(curValue);
		refreshFilters(myRoleFilter, timeframeFilter, curValue.length ? curValue : undefined, unresolvedFilter, searchInput);
	}

	function handleMyRoleFilterOptions(e: React.ChangeEvent<any>) {
		const {
			target: { value },
		} = e;

		const curValue = myRoleFilter.some((s) => s === value) ? [] : [value];
		setMyRoleFilter(curValue);
		refreshFilters(curValue.length ? curValue : undefined, timeframeFilter, categoryFilter, unresolvedFilter, searchInput);
	}

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			style={{ height: "100%" }}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box style={{ height: "100%" }}>
					<Box>
						<Box
							display="flex"
							alignItems="center"
							flexDirection={"row"}
							justifyContent="start"
							style={{
								gap: 16,
							}}
						>
							<TextField
								variant="outlined"
								InputProps={{
									classes: { root: search },
									startAdornment: <SearchIcon color="primary" />,
									endAdornment: searchInput ? (
										<IconButton onClick={clearInput}>
											<ClearIcon />
										</IconButton>
									) : (
										""
									),
								}}
								placeholder={` ${t("search")} ${t("quests")}...`}
								value={searchInput}
								onChange={handleSearchInput}
							/>

							<Chip
								label={t("unresolved")}
								icon={<Check className={checkLabel} style={{ display: unresolvedFilter ? "inline-block" : "none" }} />}
								className={assignedToMeChip}
								component="a"
								variant="outlined"
								style={{
									borderColor: unresolvedFilter ? "#367bf5" : "rgba(54, 123, 245, 0.4)",
								}}
								onClick={() => handleUnresolved()}
							/>
							<Select
								id="filter-timeframe"
								className={`${selectField} ${selectFieldQuest}`}
								variant="outlined"
								value={timeframeFilter}
								onChange={handleTimeframeFilterOptions}
								displayEmpty
								MenuProps={{
									anchorOrigin: {
										vertical: "bottom",
										horizontal: "left",
									},
									transformOrigin: {
										vertical: "top",
										horizontal: "left",
									},
									getContentAnchorEl: null,
								}}
							>
								{taskTimeframeOptions.map((c: any, i: number) => (
									<MenuItem key={i} value={c}>
										{t(c)}
									</MenuItem>
								))}
							</Select>
							<Select
								id="filter-category"
								variant="outlined"
								className={`${selectField} ${selectFieldQuest}`}
								value={categoryFilter}
								onChange={handleCategoryFilterOptions}
								displayEmpty
								MenuProps={{
									anchorOrigin: {
										vertical: "bottom",
										horizontal: "left",
									},
									transformOrigin: {
										vertical: "top",
										horizontal: "left",
									},
									getContentAnchorEl: null,
								}}
							>
								<MenuItem style={{ display: "none" }} value={""}>
									{t("category")}
								</MenuItem>
								{categoryOptions.map((c: any, i: number) => (
									<MenuItem key={i} value={c}>
										{t("quests-type-" + c)}
									</MenuItem>
								))}
							</Select>
							<Select
								id="filter-my-role"
								variant="outlined"
								displayEmpty
								className={`${selectField} ${selectFieldQuest}`}
								value={myRoleFilter}
								onChange={handleMyRoleFilterOptions}
								MenuProps={{
									anchorOrigin: {
										vertical: "bottom",
										horizontal: "left",
									},
									transformOrigin: {
										vertical: "top",
										horizontal: "left",
									},
									getContentAnchorEl: null,
								}}
							>
								{myRoleOptions.map((c: string, i: number) => (
									<MenuItem key={i} value={c}>
										{`${t(c)}`}
									</MenuItem>
								))}
							</Select>
						</Box>
					</Box>
					<Divider style={{ margin: "22px 0" }} />
					<Quests questFilter={questFilter} />
				</Box>
			)}
		</div>
	);
};

export default QuestsTabPanel;
